import { useState } from 'react';
import { Alert, App, Button, Descriptions, Input, Modal, Space, Typography } from 'antd';
import { PlusOutlined, UndoOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { newAdjustKey } from '../users/walletAdjust';

/** The API's per-credit limit (ADMIN_EARNING_CREDIT_MAX_PAISE): ₹1,00,000. */
export const EARNING_CREDIT_MAX_PAISE = 10_000_000n;
/** The API's reason length limit (ADMIN_EARNING_CREDIT_REASON_MAX). */
export const EARNING_CREDIT_REASON_MAX = 500;

export type RupeesParse = { ok: true; paise: bigint } | { ok: false; error: string };

/**
 * Typed rupees → integer paise, by STRING arithmetic — never `Number`, never a
 * float: "500" → 50000n, "500.5" → 50050n, "1,250.75" → 125075n. At most two
 * decimals; anything else is rejected with a message for the form.
 */
export function parseRupeesToPaise(input: string): RupeesParse {
  const s = input.trim().replace(/^₹\s*/, '').replace(/,/g, '');
  if (s === '') return { ok: false, error: 'Enter an amount' };
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) return { ok: false, error: 'Enter a valid amount in rupees, e.g. 500 or 500.50 (max 2 decimals)' };
  const [, whole = '0', frac = ''] = m;
  const paise = BigInt(whole) * 100n + BigInt(frac.padEnd(2, '0'));
  if (paise <= 0n) return { ok: false, error: 'Amount must be greater than ₹0' };
  if (paise > EARNING_CREDIT_MAX_PAISE) return { ok: false, error: 'One credit can be at most ₹1,00,000' };
  return { ok: true, paise };
}

/** Exact "₹1,234.50" from paise (bigint or digit string) — two decimals, en-IN grouping. */
export function formatPaiseExact(paise: bigint | string): string {
  const p = typeof paise === 'bigint' ? paise : BigInt(paise);
  const neg = p < 0n;
  const abs = neg ? -p : p;
  const rupees = (abs / 100n).toString();
  const frac = (abs % 100n).toString().padStart(2, '0');
  // Indian grouping: last three digits, then pairs.
  const head = rupees.length > 3 ? rupees.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' : '';
  const tail = rupees.length > 3 ? rupees.slice(-3) : rupees;
  return `${neg ? '-' : ''}₹${head}${tail}.${frac}`;
}

export function reasonError(reason: string): string | null {
  const r = reason.trim();
  if (r.length === 0) return 'Enter a reason';
  if (r.length > EARNING_CREDIT_REASON_MAX) return `Keep the reason under ${EARNING_CREDIT_REASON_MAX} characters`;
  return null;
}

interface CreditResponse {
  ok: true;
  duplicate: boolean;
  transactionId: string;
  amountPaise: string;
  reason: string;
  availableBalancePaise: string;
}

/**
 * "+ Credit Earnings" on Listener Details. Two steps in one dialog:
 *   1. Amount (₹) + Reason — both required, the reason is whatever the admin
 *      types (no default text);
 *   2. Confirm — the exact amount and reason, then POST.
 *
 * Money safety:
 *  • the amount goes on the wire as integer paise computed from the typed
 *    string (no floating point);
 *  • one Idempotency-Key per dialog session — a double-click, a network retry
 *    or "Confirm" pressed again after a timeout all reuse it, so the server
 *    credits once. A NEW key is minted only after a success or when the
 *    dialog is closed.
 *
 * The button only renders for `listeners.earnings.credit`; the API enforces
 * the same permission on its own.
 */
export function CreditEarningsButton({
  listenerId,
  listenerName,
}: {
  listenerId: string;
  listenerName: string;
}) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [key, setKey] = useState<string>(newAdjustKey);

  const parsed = parseRupeesToPaise(amount);
  const rErr = reasonError(reason);
  const valid = parsed.ok && rErr === null;

  function close() {
    setOpen(false);
    setStep('form');
    setAmount('');
    setReason('');
    setTouched(false);
    setKey(newAdjustKey());
  }

  const credit = useMutation({
    mutationFn: async () => {
      if (!parsed.ok) throw new Error('invalid amount');
      return (
        await api.post<CreditResponse>(
          `/admin/listeners/${listenerId}/earnings/credit`,
          // Paise as a digit string: exact for any size, accepted by the API.
          { amountPaise: parsed.paise.toString(), reason: reason.trim() },
          { headers: { 'idempotency-key': key } },
        )
      ).data;
    },
    onSuccess: (res) => {
      message.success(
        res.duplicate
          ? 'Already credited — this request was not repeated.'
          : `✓ ${formatPaiseExact(res.amountPaise)} credited successfully`,
      );
      // Balance, totals and both histories come from the server again.
      void qc.invalidateQueries({ queryKey: ['admin', 'listener-overview', listenerId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'listener-history', 'earnings', listenerId] });
      close();
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ??
        'Could not credit earnings. Nothing was credited — you can try again.';
      message.error(msg);
      // Keep the dialog, the values AND the key: "Confirm" again is a safe retry.
    },
  });

  const amountError = touched && !parsed.ok ? parsed.error : null;
  const reasonErr = touched ? rErr : null;

  return (
    <Can permission="listeners.earnings.credit">
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
        Credit Earnings
      </Button>
      <Modal
        title="Credit Listener Earnings"
        open={open}
        onCancel={credit.isPending ? undefined : close}
        maskClosable={false}
        destroyOnHidden
        footer={
          step === 'form' ? (
            <Space>
              <Button onClick={close}>Cancel</Button>
              <Button
                type="primary"
                onClick={() => {
                  setTouched(true);
                  if (valid) setStep('confirm');
                }}
              >
                Credit Amount
              </Button>
            </Space>
          ) : (
            <Space>
              <Button onClick={() => setStep('form')} disabled={credit.isPending}>
                Cancel
              </Button>
              <Button type="primary" loading={credit.isPending} onClick={() => credit.mutate()}>
                Confirm Credit
              </Button>
            </Space>
          )
        }
      >
        {step === 'form' ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Typography.Text type="secondary">Listener</Typography.Text>
              <div>
                <Typography.Text strong>{listenerName}</Typography.Text>
              </div>
            </div>
            <div>
              <label htmlFor="credit-amount">
                <Typography.Text type="secondary">Amount (₹)</Typography.Text>
              </label>
              <Input
                id="credit-amount"
                prefix="₹"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                status={amountError ? 'error' : undefined}
                onChange={(e) => setAmount(e.target.value)}
                onBlur={() => setTouched(true)}
              />
              {amountError && <Typography.Text type="danger">{amountError}</Typography.Text>}
            </div>
            <div>
              <label htmlFor="credit-reason">
                <Typography.Text type="secondary">Reason</Typography.Text>
              </label>
              <Input.TextArea
                id="credit-reason"
                rows={3}
                maxLength={EARNING_CREDIT_REASON_MAX}
                showCount
                placeholder="Why is this amount being credited? The listener sees this text."
                value={reason}
                status={reasonErr ? 'error' : undefined}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setTouched(true)}
              />
              {reasonErr && <Typography.Text type="danger">{reasonErr}</Typography.Text>}
            </div>
            <Alert
              type="info"
              showIcon
              message="Adds withdrawable ₹ to this listener's earnings"
              description={
                <span style={{ fontSize: 12 }}>
                  Recorded as an Admin Credit (never a call earning), with your name and this reason, in the
                  audit log and the listener&apos;s earnings history.
                </span>
              }
            />
          </Space>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Credit {parsed.ok ? formatPaiseExact(parsed.paise) : '—'} to {listenerName}?
            </Typography.Title>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Amount">{parsed.ok ? formatPaiseExact(parsed.paise) : '—'}</Descriptions.Item>
              <Descriptions.Item label="Reason">
                <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>“{reason.trim()}”</Typography.Text>
              </Descriptions.Item>
            </Descriptions>
          </Space>
        )}
      </Modal>
    </Can>
  );
}

interface ReversalResponse {
  ok: true;
  duplicate: boolean;
  reversalId: string;
  amountPaise: string;
  remainingPaise: string;
  availableBalancePaise: string;
}

/**
 * "Reverse" on an ADMIN CREDIT row of the earnings history: takes back (part
 * of) that one credit from the listener's earnings wallet. The admin types the
 * amount — at most what is still un-reversed on the credit — and a reason,
 * then confirms.
 *
 * The server is the judge of both limits (never more than is left on the
 * credit, never below a ₹0 balance); its refusal is shown as-is. Same
 * Idempotency-Key discipline as {@link CreditEarningsButton}.
 */
export function ReverseCreditButton({
  listenerId,
  listenerName,
  transactionId,
  creditedPaise,
  remainingPaise,
}: {
  listenerId: string;
  listenerName: string;
  /** The admin credit's transaction id (the row's referenceId). */
  transactionId: string;
  creditedPaise: string;
  remainingPaise: string;
}) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [key, setKey] = useState<string>(newAdjustKey);

  const remaining = BigInt(remainingPaise);
  const parsedRaw = parseRupeesToPaise(amount);
  const parsed: RupeesParse =
    parsedRaw.ok && parsedRaw.paise > remaining
      ? { ok: false, error: `Only ${formatPaiseExact(remaining)} of this credit is left to reverse` }
      : parsedRaw;
  const rErr = reasonError(reason);
  const valid = parsed.ok && rErr === null;

  function close() {
    setOpen(false);
    setStep('form');
    setAmount('');
    setReason('');
    setTouched(false);
    setKey(newAdjustKey());
  }

  const reverse = useMutation({
    mutationFn: async () => {
      if (!parsed.ok) throw new Error('invalid amount');
      return (
        await api.post<ReversalResponse>(
          `/admin/listeners/${listenerId}/earnings/credit/${transactionId}/reverse`,
          { amountPaise: parsed.paise.toString(), reason: reason.trim() },
          { headers: { 'idempotency-key': key } },
        )
      ).data;
    },
    onSuccess: (res) => {
      message.success(
        res.duplicate
          ? 'Already reversed — this request was not repeated.'
          : `✓ ${formatPaiseExact(res.amountPaise)} reversed. Balance now ${formatPaiseExact(res.availableBalancePaise)}`,
      );
      void qc.invalidateQueries({ queryKey: ['admin', 'listener-overview', listenerId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'listener-history', 'earnings', listenerId] });
      close();
    },
    onError: (e: unknown) => {
      const data = (e as { response?: { data?: { code?: string; message?: string } } }).response?.data;
      message.error(
        data?.code === 'INSUFFICIENT_EARNINGS_BALANCE'
          ? "The listener's available balance is less than this amount (already paid out or held for a payout). Nothing was reversed."
          : (data?.message ?? 'Could not reverse. Nothing was reversed — you can try again.'),
      );
      // Keep the dialog, the values AND the key: "Confirm" again is a safe retry.
    },
  });

  const amountError = touched && !parsed.ok ? parsed.error : null;
  const reasonErr = touched ? rErr : null;

  return (
    <Can permission="listeners.earnings.credit">
      <Button size="small" danger icon={<UndoOutlined />} onClick={() => setOpen(true)}>
        Reverse
      </Button>
      <Modal
        title="Reverse Admin Credit"
        open={open}
        onCancel={reverse.isPending ? undefined : close}
        maskClosable={false}
        destroyOnHidden
        footer={
          step === 'form' ? (
            <Space>
              <Button onClick={close}>Cancel</Button>
              <Button
                type="primary"
                danger
                onClick={() => {
                  setTouched(true);
                  if (valid) setStep('confirm');
                }}
              >
                Reverse Amount
              </Button>
            </Space>
          ) : (
            <Space>
              <Button onClick={() => setStep('form')} disabled={reverse.isPending}>
                Cancel
              </Button>
              <Button type="primary" danger loading={reverse.isPending} onClick={() => reverse.mutate()}>
                Confirm Reversal
              </Button>
            </Space>
          )
        }
      >
        {step === 'form' ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Listener">{listenerName}</Descriptions.Item>
              <Descriptions.Item label="Credited">{formatPaiseExact(creditedPaise)}</Descriptions.Item>
              <Descriptions.Item label="Left to reverse">{formatPaiseExact(remaining)}</Descriptions.Item>
            </Descriptions>
            <div>
              <label htmlFor="reverse-amount">
                <Typography.Text type="secondary">Amount to reverse (₹)</Typography.Text>
              </label>
              <Input
                id="reverse-amount"
                prefix="₹"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                status={amountError ? 'error' : undefined}
                onChange={(e) => setAmount(e.target.value)}
                onBlur={() => setTouched(true)}
              />
              {amountError && <Typography.Text type="danger">{amountError}</Typography.Text>}
            </div>
            <div>
              <label htmlFor="reverse-reason">
                <Typography.Text type="secondary">Reason</Typography.Text>
              </label>
              <Input.TextArea
                id="reverse-reason"
                rows={3}
                maxLength={EARNING_CREDIT_REASON_MAX}
                showCount
                placeholder="Why is this amount being taken back?"
                value={reason}
                status={reasonErr ? 'error' : undefined}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setTouched(true)}
              />
              {reasonErr && <Typography.Text type="danger">{reasonErr}</Typography.Text>}
            </div>
            <Alert
              type="warning"
              showIcon
              message="Deducts ₹ from this listener's earnings wallet"
              description={
                <span style={{ fontSize: 12 }}>
                  Refused if her available balance is lower than the amount. Recorded with your name and this
                  reason in the audit log and the earnings history.
                </span>
              }
            />
          </Space>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Take back {parsed.ok ? formatPaiseExact(parsed.paise) : '—'} from {listenerName}?
            </Typography.Title>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Amount">{parsed.ok ? formatPaiseExact(parsed.paise) : '—'}</Descriptions.Item>
              <Descriptions.Item label="Reason">
                <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>“{reason.trim()}”</Typography.Text>
              </Descriptions.Item>
            </Descriptions>
          </Space>
        )}
      </Modal>
    </Can>
  );
}
